import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from '../../../../../util/reactIntl';
import {
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
} from '../../../../../components';
import IconCard from '../../../../../components/SavedCardDetails/IconCard/IconCard';
import { updateProfile } from '../../../../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';

import css from './LanguageMenu.module.css';

// Import translation files
import enTranslations from '../../../../../translations/en.json';
import arTranslations from '../../../../../translations/ar.json';

const LanguageMenu = ({ currentPage, isMobile = false, className = null }) => {
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user.currentUser);

  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isOpen, setIsOpen] = useState(false);

  const userLangPref =
    currentUser?.attributes?.profile?.publicData?.languagePreference ||
    currentUser?.attributes?.profile?.protectedData?.languagePreference;

  useEffect(() => {
    // Get saved language from localStorage
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';
    setCurrentLanguage(savedLanguage);
    // Listen for global languageChange (footer or other triggers)
    const onLanguageChange = e => {
      const next = e?.detail?.locale === 'ar' ? 'ar' : 'en';
      setCurrentLanguage(next);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('languageChange', onLanguageChange);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('languageChange', onLanguageChange);
      }
    };
  }, []);

  useEffect(() => {
    if (userLangPref && userLangPref !== currentLanguage) {
      handleLanguageChange(userLangPref);
    }
  }, [userLangPref]);

  const handleLanguageChange = (language) => {
    setIsOpen(false);
    setCurrentLanguage(language);
    switchLanguage({
      language,
      history,
      location,
      currentPage,
      currentUser,
      dispatch,
    });
  };

  const displayLanguage = currentLanguage === 'ar' ? 'AR' : 'EN';

  return (
    <Menu
      contentPosition={isMobile ? 'right' : 'left'}
      isOpen={isOpen}
      onToggleActive={setIsOpen}
    >
      <MenuLabel className={css.languageMenuLabel} isOpenClassName={css.languageMenuIsOpen}>
        <div
          className={classNames(css.languageSelector, className, {
            [css.mobileLanguageSelector]: isMobile,
          })}
        >
          <IconCard type="globe" />
          <span>{displayLanguage}</span>
        </div>
      </MenuLabel>
      <MenuContent className={classNames(css.languageMenuContent, { [css.mobileMenuContent]: isMobile })}>
        <MenuItem key="english">
          <button 
            className={css.languageOption} 
            onClick={() => handleLanguageChange('en')}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.languageEnglish" />
          </button>
        </MenuItem>
        <MenuItem key="arabic">
          <button 
            className={css.languageOption} 
            onClick={() => handleLanguageChange('ar')}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.languageArabic" />
          </button>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

export const switchLanguage = ({
  language,
  history,
  location,
  currentPage,
  currentUser,
  dispatch,
}) => {
  const userLangPref =
    currentUser?.attributes?.profile?.publicData?.languagePreference ||
    currentUser?.attributes?.profile?.protectedData?.languagePreference;

  try {
    localStorage.setItem('selectedLanguage', language);
  } catch (_) {}

  if (currentUser?.id && language !== userLangPref && dispatch) {
    dispatch(updateProfile({ publicData: { languagePreference: language } })).catch(e => {
      console.warn('Failed to update language on user profile:', e);
    });
  }

  // Update the translations dynamically
  const translations = language === 'ar' ? arTranslations : enTranslations;

  // Store translations in window for app.js to pick up
  window.__LANGUAGE_CHANGE__ = {
    locale: language === 'ar' ? 'ar' : 'en-US',
    messages: translations,
  };

  const currentPath = location
    ? `${location.pathname}${location.search}${location.hash}`
    : typeof window !== 'undefined'
    ? `${window.location.pathname}${window.location.search}${window.location.hash}`
    : '/';
  const pathname = location?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const isOnEnglishLanding = pathname === '/' || currentPage === 'LandingPage';
  const isOnArabicLanding = pathname === '/p/ar-home' || currentPage === 'CMSPage:ar-home';

  if (language === 'ar') {
    document.documentElement.setAttribute('dir', 'rtl');
    document.documentElement.setAttribute('lang', 'ar');
    if (isOnEnglishLanding && history) {
      history.push('/p/ar-home');
    } else if (history) {
      history.replace(currentPath);
    }
  } else {
    document.documentElement.setAttribute('dir', 'ltr');
    document.documentElement.setAttribute('lang', 'en');
    if (isOnArabicLanding && history) {
      history.push('/');
    } else if (history) {
      history.replace(currentPath);
    }
  }

  // Notify app to update IntlProvider (no reload)
  try {
    window.dispatchEvent(
      new CustomEvent('languageChange', { detail: { locale: language === 'ar' ? 'ar' : 'en' } })
    );
  } catch (_) {}
};

export default LanguageMenu;
